import React from 'react';
import styled, { keyframes } from 'styled-components';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  overlay?: boolean;
  message?: string;
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerContainer = styled.div<{ overlay: boolean; size: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  
  ${props => props.overlay && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    z-index: 9999;
  `}
  
  ${props => !props.overlay && props.size === 'small' && `
    padding: 1rem;
  `}
  
  ${props => !props.overlay && props.size === 'medium' && `
    padding: 2rem;
    min-height: 200px;
  `}
  
  ${props => !props.overlay && props.size === 'large' && `
    padding: 4rem;
    min-height: 400px;
  `}
`;

const Spinner = styled.div<{ size: string }>`
  border: 4px solid var(--color-gray-200);
  border-top: 4px solid var(--color-primary);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  
  ${props => props.size === 'small' && `
    width: 24px;
    height: 24px;
    border-width: 2px;
  `}
  
  ${props => props.size === 'medium' && `
    width: 40px;
    height: 40px;
    border-width: 3px;
  `}
  
  ${props => props.size === 'large' && `
    width: 60px;
    height: 60px;
    border-width: 4px;
  `}
`;

const LoadingText = styled.p<{ size: string }>`
  color: var(--color-gray-700);
  font-family: var(--font-sans);
  font-weight: 500;
  margin: 0;
  
  ${props => props.size === 'small' && `
    font-size: 0.875rem;
  `}
  
  ${props => props.size === 'medium' && `
    font-size: 1rem;
  `}
  
  ${props => props.size === 'large' && `
    font-size: 1.125rem;
  `}
`;

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'medium', 
  overlay = false, 
  message = 'Loading...' 
}) => {
  return (
    <SpinnerContainer overlay={overlay} size={size}>
      <Spinner size={size} />
      {message && <LoadingText size={size}>{message}</LoadingText>}
    </SpinnerContainer>
  );
};

export default LoadingSpinner;